import { isNonMvpRouteEnabled } from '../config/routeGates'

export type PostAuthRouteKey =
  | 'dashboard'
  | 'inbox'
  | 'deals'
  | 'dealDetail'
  | 'dueDiligence'
  | 'ndaCenter'
  | 'buyers'
  | 'buyerDetail'
  | 'sellers'
  | 'sellerDetail'
  | 'listings'
  | 'listingDetail'
  | 'team'
  | 'activities'
  | 'tasks'
  | 'analytics'
  | 'profile'
  | 'admin'
  | 'adminDataQuality'
  | 'adminCommSetup'
  | 'advisorTools'
  | 'marketingKits'
  | 'outreach'
  | 'acceptInvite'
  | 'settingsChannels'

export type PostAuthRouteDefinition = {
  key: PostAuthRouteKey
  canonicalPath: string
  aliasPaths?: string[]
  dynamicPattern?: RegExp
  gatePath?: string
}

export const postAuthRouteDefinitions: PostAuthRouteDefinition[] = [
  { key: 'dashboard', canonicalPath: '/dashboard' },
  { key: 'inbox', canonicalPath: '/inbox' },
  { key: 'deals', canonicalPath: '/deals', aliasPaths: ['/leads'] },
  { key: 'dealDetail', canonicalPath: '/deals/:id', dynamicPattern: /^\/deals\/[^/]+$/ },
  { key: 'dueDiligence', canonicalPath: '/due-diligence', gatePath: '/due-diligence' },
  { key: 'ndaCenter', canonicalPath: '/nda-center' },
  { key: 'buyers', canonicalPath: '/buyers' },
  { key: 'buyerDetail', canonicalPath: '/buyers/:id', dynamicPattern: /^\/buyers\/[^/]+$/ },
  { key: 'sellers', canonicalPath: '/sellers' },
  { key: 'sellerDetail', canonicalPath: '/sellers/:id', dynamicPattern: /^\/sellers\/[^/]+$/ },
  { key: 'listings', canonicalPath: '/listings' },
  { key: 'listingDetail', canonicalPath: '/listings/:id', dynamicPattern: /^\/listings\/[^/]+$/ },
  { key: 'team', canonicalPath: '/team', gatePath: '/team' },
  { key: 'activities', canonicalPath: '/activities' },
  { key: 'tasks', canonicalPath: '/tasks' },
  { key: 'analytics', canonicalPath: '/analytics' },
  { key: 'profile', canonicalPath: '/profile', aliasPaths: ['/account', '/settings', '/theme'] },
  { key: 'admin', canonicalPath: '/admin' },
  { key: 'adminDataQuality', canonicalPath: '/admin/data-quality' },
  { key: 'adminCommSetup', canonicalPath: '/admin/comm-setup' },
  { key: 'advisorTools', canonicalPath: '/advisor-tools', aliasPaths: ['/kits', '/templates', '/kit-generator'] },
  { key: 'marketingKits', canonicalPath: '/marketing-kits' },
  { key: 'outreach', canonicalPath: '/outreach', aliasPaths: ['/offering-memorandums'], gatePath: '/outreach' },
  { key: 'acceptInvite', canonicalPath: '/accept-invite' },
  { key: 'settingsChannels', canonicalPath: '/settings/channels' },
]

export function normalizePostAuthPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/'
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

export const postAuthCanonicalRoutePaths = postAuthRouteDefinitions.map((route) => route.canonicalPath)

export const postAuthAliasPaths = postAuthRouteDefinitions.flatMap((route) => route.aliasPaths ?? [])

function matchesRouteDefinition(definition: PostAuthRouteDefinition, normalizedPath: string): boolean {
  if (definition.dynamicPattern) {
    return definition.dynamicPattern.test(normalizedPath)
  }

  return normalizedPath === definition.canonicalPath || definition.aliasPaths?.includes(normalizedPath) === true
}

export function getPostAuthRouteDefinition(pathname: string): PostAuthRouteDefinition | null {
  const normalizedPath = normalizePostAuthPath(pathname)
  return postAuthRouteDefinitions.find((definition) => matchesRouteDefinition(definition, normalizedPath)) ?? null
}

export function isPostAuthRoutePath(pathname: string): boolean {
  return getPostAuthRouteDefinition(pathname) !== null
}

export function isPostAuthRouteRenderablePath(pathname: string): boolean {
  const normalizedPath = normalizePostAuthPath(pathname)
  const definition = getPostAuthRouteDefinition(normalizedPath)

  if (!definition) return false
  if (!definition.gatePath) return true

  return isNonMvpRouteEnabled(normalizedPath)
}
